import { v6 as uuid } from 'uuid';
import type { Dispatch, SetStateAction } from 'react';
import type {
  ChatMessage,
  ThinkingSection,
  ThinkingSectionItem,
  ThinkingSectionItemType,
} from '@/aiAssistant/aiAssistant.types';

// ─── TrackedAbortController ───────────────────────────────────────────────────

/**
 * Extends AbortController with a unique numeric ID so that individual stream
 * operations can be tracked and distinguished when multiple concurrent streams
 * exist for the same chat session.
 */
export class TrackedAbortController extends AbortController {
  static counter = 0;
  private _id: number;

  constructor() {
    super();
    this._id = ++TrackedAbortController.counter;
  }

  get id(): number {
    return this._id;
  }
}

// ─── Stream Decoding ──────────────────────────────────────────────────────────

/**
 * Async generator that converts a ReadableStream of binary data into decoded
 * text chunks. Yields one string per chunk as it arrives from the stream.
 */
export async function* decodeStreamToText(stream: ReadableStream<Uint8Array> | null): AsyncIterableIterator<string> {
  if (!stream) return;

  const reader = stream.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    if (value) {
      try {
        yield decoder.decode(value);
      } catch (error) {
        console.error(error);
      }
    }
  }
}

// ─── Controller Registry ──────────────────────────────────────────────────────

export function hasActiveControllers(chatId: string, controllers: Record<string, TrackedAbortController[]>): boolean {
  return chatId in controllers && controllers[chatId].some((controller) => controller.signal.aborted === false);
}

export function lastActiveController(
  chatId: string,
  controllers: Record<string, TrackedAbortController[]>,
): TrackedAbortController | null {
  if (chatId in controllers) {
    const sorted = [...controllers[chatId]].sort((a, b) => b.id - a.id);
    return sorted.length > 0 ? sorted[0] : null;
  }
  return null;
}

export function addController(
  chatId: string,
  controller: TrackedAbortController,
  controllers: Record<string, TrackedAbortController[]>,
) {
  controllers[chatId] = controllers[chatId] || [];
  controllers[chatId].push(controller);
}

export function removeController(
  chatId: string,
  controllerId: number,
  controllers: Record<string, TrackedAbortController[]>,
) {
  if (!(chatId in controllers)) return;
  controllers[chatId] = controllers[chatId].filter((c) => c.id !== controllerId);
  if (controllers[chatId].length === 0) {
    delete controllers[chatId];
  }
}

// ─── Message Helpers ──────────────────────────────────────────────────────────

/**
 * Returns the message array unchanged if it is non-empty, or creates a
 * placeholder bot message as the first element. This ensures callers can
 * always append to the last message without undefined-behaviour.
 */
export function ensureLastBotMessage(chatId: string, previousMessages: ChatMessage[]): ChatMessage[] {
  if (!previousMessages.length) {
    const newMessage: ChatMessage = {
      role: 'bot',
      dialog: '',
      id: uuid(),
      chatId,
      stages: {},
      originDetails: { label: '', url: '' },
    };
    return [newMessage];
  }
  // Spread to avoid mutating a Flux frozen array
  return [...previousMessages];
}

/**
 * Applies `updateFn` to the last bot message for the given chatId.
 *
 * @param updateFn - Receives the current last message and returns a partial
 *   update to merge into it. Return `null` to signal that no update is needed;
 * @param isAborted - Returns true when the current stream operation has been
 *   aborted. Updates are skipped unless `updateAborted` is true.
 */
export function updateLastBotMessage(
  chatId: string,
  updateFn: (msg: ChatMessage) => Partial<ChatMessage> | null,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  isAborted: () => boolean,
  updateAborted = false,
): void {
  if (!updateAborted && isAborted()) {
    return;
  }

  setMessages((previousMessages) => {
    const newMessages = ensureLastBotMessage(chatId, previousMessages);
    const lastIndex = newMessages.length - 1;
    const lastMessage = newMessages[lastIndex];
    if (lastMessage.chatId !== chatId) {
      // Multiple concurrent chats — bail without modifying the wrong message
      return previousMessages;
    }

    const updates = updateFn(lastMessage);
    // null signals "nothing to update" — return the original reference so React skips the re-render
    if (updates === null) return previousMessages;
    newMessages[lastIndex] = { ...lastMessage, ...updates };

    return [...newMessages];
  });
}

// ─── Text Stream Processing ──────────────────────────────────────────────────

/**
 * Reads a text stream and appends each decoded chunk to the last bot message's
 * dialog. This is the shared "processTextStream" loop used by both hooks.
 */
export async function handleTextStreamResponse(
  chatId: string,
  stream: ReadableStream<Uint8Array> | null,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  isAborted: () => boolean,
): Promise<void> {
  if (!stream) return;

  for await (const chunk of decodeStreamToText(stream)) {
    if (isAborted()) break;
    updateLastBotMessage(
      chatId,
      (lastMessage) => ({ dialog: (lastMessage.dialog || '') + chunk }),
      setMessages,
      isAborted,
    );
  }
}

// ─── Thinking Section Helpers ─────────────────────────────────────────────────

export function addOrUpdateThinkingSection(
  chatId: string,
  sectionData: Partial<ThinkingSection> & { id: string },
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  isAborted: () => boolean,
): void {
  updateLastBotMessage(
    chatId,
    (lastMessage) => {
      const existingSections = lastMessage.thinkingSections || [];
      const sectionIndex = existingSections.findIndex((s) => s.id === sectionData.id);

      if (sectionIndex >= 0) {
        const existingSection = existingSections[sectionIndex];
        const updatedSections = [...existingSections];
        updatedSections[sectionIndex] = {
          ...existingSection,
          ...sectionData,
          // Preserve original startTime when updating an existing section
          startTime: existingSection.startTime || Date.now(),
        };
        return { thinkingSections: updatedSections };
      } else {
        const newSection: ThinkingSection = {
          title: '',
          status: 'running',
          items: [],
          startTime: Date.now(),
          ...sectionData,
        };
        return { thinkingSections: [...existingSections, newSection] };
      }
    },
    setMessages,
    isAborted,
  );
}

export function addOrUpdateThinkingSectionItem(
  chatId: string,
  sectionId: string,
  itemData: Partial<ThinkingSectionItem> & { item_id: string },
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  isAborted: () => boolean,
): void {
  updateLastBotMessage(
    chatId,
    (lastMessage) => {
      const existingSections = lastMessage.thinkingSections || [];
      const sectionIndex = existingSections.findIndex((s) => s.id === sectionId);

      if (sectionIndex >= 0) {
        const section = existingSections[sectionIndex];
        const items = section.items || [];
        const itemIndex = items.findIndex((item) => item.item_id === itemData.item_id);

        const updatedSections = [...existingSections];

        if (itemIndex >= 0) {
          const updatedItems = [...items];
          const currentItem = updatedItems[itemIndex];

          // Only update fields that are explicitly provided (not undefined)
          const updatedItem: ThinkingSectionItem = {
            ...currentItem,
            ...(itemData.name !== undefined && { name: itemData.name }),
            ...(itemData.type !== undefined && { type: itemData.type }),
            ...(itemData.description !== undefined && { description: itemData.description }),
            ...(itemData.content !== undefined && { content: itemData.content }),
            ...(itemData.metadata !== undefined && { metadata: itemData.metadata }),
            ...(itemData.status !== undefined && { status: itemData.status }),
            startTime: currentItem.startTime || Date.now(),
            endTime:
              itemData.status === 'completed' || itemData.status === 'error'
                ? currentItem.endTime || Date.now()
                : currentItem.endTime,
          };

          updatedItems[itemIndex] = updatedItem;
          updatedSections[sectionIndex] = { ...section, items: updatedItems };
        } else {
          const newItem: ThinkingSectionItem = {
            item_id: itemData.item_id,
            name: itemData.name || '',
            type: itemData.type,
            description: itemData.description || '',
            status: itemData.status || 'running',
            startTime: Date.now(),
            content: itemData.content,
            metadata: itemData.metadata || {},
            endTime: itemData.status === 'completed' || itemData.status === 'error' ? Date.now() : undefined,
          };
          updatedSections[sectionIndex] = { ...section, items: [...items, newItem] };
        }

        return { thinkingSections: updatedSections };
      }

      return {};
    },
    setMessages,
    isAborted,
  );
}

/**
 * Appends a streaming delta to an existing thinking section item's content.
 * Returns `null` when the section or item is not found so that
 * `updateLastBotMessage` can short-circuit and preserve the original array
 * reference, avoiding an unnecessary React re-render.
 */
export function appendThinkingSectionItemContent(
  chatId: string,
  sectionId: string,
  itemId: string,
  delta: string,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  isAborted: () => boolean,
): void {
  updateLastBotMessage(
    chatId,
    (lastMessage) => {
      const existingSections = lastMessage.thinkingSections || [];
      const sectionIndex = existingSections.findIndex((s) => s.id === sectionId);
      if (sectionIndex < 0) return null;

      const section = existingSections[sectionIndex];
      const items = section.items || [];
      const itemIndex = items.findIndex((item) => item.item_id === itemId);
      if (itemIndex < 0) return null;

      const updatedItems = [...items];
      const currentItem = updatedItems[itemIndex];
      updatedItems[itemIndex] = { ...currentItem, content: (currentItem.content || '') + delta };

      const updatedSections = [...existingSections];
      updatedSections[sectionIndex] = { ...section, items: updatedItems };
      return { thinkingSections: updatedSections };
    },
    setMessages,
    isAborted,
  );
}

// ─── Thinking Section Error Finalization ──────────────────────────────────────

/**
 * Marks any still-running thinking sections (and their items) as errored.
 * Called from both hooks' catch blocks to ensure the UI never shows a spinner
 * for a section that will never complete.
 *
 * Always runs regardless of abort state — a cancelled stream still needs its
 * UI cleaned up so spinners never get stuck.
 */
export function finalizeThinkingSectionsOnError(
  chatId: string,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
): void {
  updateLastBotMessage(
    chatId,
    (lastMessage) => ({
      thinkingSections: (lastMessage.thinkingSections || []).map((section) => ({
        ...section,
        status: section.status === 'running' ? 'error' : section.status,
        items: (section.items || []).map((item) => ({
          ...item,
          status: item.status === 'running' ? 'error' : item.status,
        })),
      })),
    }),
    setMessages,
    () => false,
    true,
  );
}

// ─── Thinking Stream Parser ───────────────────────────────────────────────────

/**
 * Callback invoked for every parsed thinking-stream event, including events
 * not handled by the shared parser (e.g. `stage`, `memory_added`, node-status
 * dispatches). Fires after the shared state updates for a given event type.
 */
export type ThinkingStreamEventCallback = (eventType: string, data: Record<string, unknown>) => void;

/**
 * Reads a JSONL thinking stream and dispatches shared thinking-section events.
 * Hook-specific side effects (stage tags, memory events, canvas node status)
 * are delegated to the optional `onEvent` callback.
 */
export async function handleThinkingStreamResponse(
  chatId: string,
  thinkingStream: ReadableStream<Uint8Array>,
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
  isAborted: () => boolean,
  onEvent?: ThinkingStreamEventCallback,
): Promise<void> {
  let buffer = '';

  for await (const chunk of decodeStreamToText(thinkingStream)) {
    if (isAborted()) break;

    buffer += chunk;

    let newlineIndex = buffer.indexOf('\n');
    while (newlineIndex >= 0) {
      const line = buffer.substring(0, newlineIndex).trim();
      buffer = buffer.substring(newlineIndex + 1);

      try {
        const data: Record<string, unknown> = line ? JSON.parse(line) : { type: 'invalid' };
        const type = data.type as string;

        switch (type) {
          case 'thinking_section_start': {
            addOrUpdateThinkingSection(
              chatId,
              { id: data.section_id as string, title: (data.title as string) || 'Processing...', status: 'running' },
              setMessages,
              isAborted,
            );
            if (data.item_id) {
              addOrUpdateThinkingSectionItem(
                chatId,
                data.section_id as string,
                {
                  item_id: data.item_id as string,
                  name: data.item_name as string,
                  type: data.item_type as ThinkingSectionItemType | undefined,
                  description: (data.item_description as string) || '',
                  status: 'running',
                  content: data.item_content as string | undefined,
                  metadata: (data.item_metadata as Record<string, unknown>) || {},
                },
                setMessages,
                isAborted,
              );
            }
            onEvent?.(type, data);
            break;
          }

          case 'thinking_section_complete': {
            if (Array.isArray(data.completed_items)) {
              data.completed_items.forEach((completedItem: Record<string, unknown>) => {
                const itemId =
                  (completedItem.item_id as string) || `completed_${completedItem.name || 'item'}_${Date.now()}`;
                addOrUpdateThinkingSectionItem(
                  chatId,
                  data.section_id as string,
                  {
                    item_id: itemId,
                    name: completedItem.name as string,
                    type: completedItem.item_type as ThinkingSectionItemType | undefined,
                    description: (completedItem.description as string) || '',
                    content: (completedItem.content as string) || '',
                    metadata: (completedItem.metadata as Record<string, unknown>) || {},
                    status: 'completed',
                  },
                  setMessages,
                  isAborted,
                );
              });
            }
            addOrUpdateThinkingSection(
              chatId,
              {
                id: data.section_id as string,
                status: data.error === true ? 'error' : 'completed',
                endTime: Date.now(),
              },
              setMessages,
              isAborted,
            );
            onEvent?.(type, data);
            break;
          }

          case 'thinking_item_start': {
            addOrUpdateThinkingSectionItem(
              chatId,
              data.section_id as string,
              {
                item_id: data.item_id as string,
                name: data.name as string,
                type: data.item_type as ThinkingSectionItemType | undefined,
                description: (data.description as string) || '',
                status: 'running',
                content: data.content as string | undefined,
                metadata: (data.metadata as Record<string, unknown>) || {},
              },
              setMessages,
              isAborted,
            );
            onEvent?.(type, data);
            break;
          }

          case 'thinking_item_update': {
            addOrUpdateThinkingSectionItem(
              chatId,
              data.section_id as string,
              {
                item_id: data.item_id as string,
                name: data.name as string | undefined,
                type: data.item_type as ThinkingSectionItemType | undefined,
                description: data.description as string | undefined,
                content: data.content as string | undefined,
                metadata: (data.metadata as Record<string, unknown>) || {},
                status: 'running',
              },
              setMessages,
              isAborted,
            );
            onEvent?.(type, data);
            break;
          }

          case 'thinking_item_complete': {
            addOrUpdateThinkingSectionItem(
              chatId,
              data.section_id as string,
              {
                item_id: data.item_id as string,
                name: data.name as string | undefined,
                type: data.item_type as ThinkingSectionItemType | undefined,
                description: data.description as string | undefined,
                content: data.content as string | undefined,
                metadata: (data.metadata as Record<string, unknown>) || {},
                status: 'completed',
              },
              setMessages,
              isAborted,
            );
            onEvent?.(type, data);
            break;
          }

          case 'thinking_item_content_delta': {
            appendThinkingSectionItemContent(
              chatId,
              data.section_id as string,
              data.item_id as string,
              data.delta as string,
              setMessages,
              isAborted,
            );
            onEvent?.(type, data);
            break;
          }

          case 'thinking_item_error': {
            addOrUpdateThinkingSectionItem(
              chatId,
              data.section_id as string,
              {
                item_id: data.item_id as string,
                name: data.name as string | undefined,
                type: data.item_type as ThinkingSectionItemType | undefined,
                description: (data.description as string) || 'Error occurred',
                content: (data.content as string) || (data.error as string) || 'An error occurred during processing',
                metadata: (data.metadata as Record<string, unknown>) || {},
                status: 'error',
              },
              setMessages,
              isAborted,
            );
            onEvent?.(type, data);
            break;
          }

          default:
            // Forward unrecognized events to the hook-specific callback
            onEvent?.(type, data);
            break;
        }
      } catch (e) {
        // Incomplete JSON — prepend the line back and wait for more data
        buffer = `${line}\n${buffer}`;
        break;
      }

      newlineIndex = buffer.indexOf('\n');
    }
  }
}
